import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ConfirmDialogProvider } from "./components/shared/ConfirmDialog";

// Auth
import Login from "./components/auth/Login";
import Register from "./components/auth/Register";

// Layout
import MainLayout from "./components/layout/MainLayout";

// Admin Pages
import AdminDashboard from "./components/admin/Dashboard/AdminDashboard";
import EmployeesPage from "./components/admin/Employees/EmployeesPage";
import AttendanceManager from "./components/admin/Employees/AttendanceManager";
import AppraisalsPage from "./components/admin/Appraisals/AppraisalsPage";
import TemplateBuilder from "./components/admin/Templates/TemplateBuilder";
import FeedbackLibrary from "./components/admin/Library/FeedbackLibrary";
import AdminFeedbackPage from "./components/admin/Feedback/AdminFeedbackPage";
import ReportsPage from "./components/admin/Reports/ReportsPage";
import InvitesPage from "./components/admin/Invites/InvitesPage";
import CoursesPage from "./components/admin/Learning/CoursesPage";

// Admin Payroll Pages
import {
  SalaryStructures,
  RunPayroll,
  BonusManagement,
  ReimbursementManagement,
  PayrollSettings,
  PayslipViewer,
  PayrollReports,
  // US Payroll Components (Unified)
  USPayrollRun,
  USPayrollReports,
  CompanyTaxSettings,
  EmployeeCompensationSetup,
  TaxLiabilityManagement,
  YearEndForms,
  PayGroupManagement,
  PayCodeManagement,
  UnifiedPayrollDashboard,
  PayrollAutomation,
  StateTaxSettings,
  MonthlyAttendanceManagement,
} from "./components/admin/Payroll";

// Admin Finance
import FinanceDashboard from "./components/admin/Finance/FinanceDashboard";

// CEO Pages
import CEODashboard from "./components/ceo/Dashboard/CEODashboard";
import CEOAppraisalsPage from "./components/ceo/Appraisals/CEOAppraisalsPage";
import CEOAppraisalReview from "./components/ceo/Appraisals/CEOAppraisalReview";
import CEOAssignAppraisal from "./components/ceo/Appraisals/CEOAssignAppraisal";
import CEOTeamPage from "./components/ceo/Team/CEOTeamPage";
import CEOFeedbackPage from "./components/ceo/Feedback/CEOFeedbackPage";

// CEO Payroll Pages
import { PayrollOverview as CEOPayrollOverview, ReimbursementReview as CEOReimbursementReview, LeaveReview as CEOLeaveReview } from "./components/ceo/Payroll";

// Supervisor Pages
import SupervisorDashboard from "./components/supervisor/Dashboard/SupervisorDashboard";
import TeamPage from "./components/supervisor/Team/TeamPage";
import LeaveManagement from "./components/supervisor/Team/LeaveManagement";
import TeamAppraisalsPage from "./components/supervisor/Appraisals/TeamAppraisalsPage";
import TeamCoursesPage from "./components/supervisor/Learning/TeamCoursesPage";
import SupervisorGoalsPage from "./components/supervisor/Goals/SupervisorGoalsPage";
import SupervisorAppraisalReview from "./components/supervisor/Appraisals/SupervisorAppraisalReview";
import SupervisorFeedbackPage from "./components/supervisor/Feedback/SupervisorFeedbackPage";

// Supervisor Payroll Pages
import { TeamPayroll, ApproveReimbursements } from "./components/supervisor/Payroll";

// Employee Pages
import EmployeeDashboard from "./components/employee/Dashboard/EmployeeDashboard";
import MyAppraisalsPage from "./components/employee/Appraisals/MyAppraisalsPage";
import MyCoursesPage from "./components/employee/Learning/MyCoursesPage";
import AppraisalDetail from "./components/employee/Appraisal/AppraisalDetail";
import GoalsPage from "./components/employee/Goals/GoalsPage";
import FeedbackPage from "./components/employee/Feedback/FeedbackPage";

// Employee Payroll Pages
import {
  MyPayslips,
  MySalaryInfo,
  ReimbursementRequest,
  LeaveBalance,
} from "./components/employee/Payroll";

// Shared Pages
import CalendarPage from "./components/shared/CalendarPage";
import SettingsPage from "./components/shared/SettingsPage";
import AttendancePage from "./components/shared/AttendancePage";
import EmployeeOnboardingSimplified from "./components/shared/EmployeeOnboardingSimplified";
import ComplianceOnboardingWizard from "./components/shared/ComplianceOnboardingWizard";

// Compliance Pages
import ComplianceDashboard from "./components/admin/Compliance/ComplianceDashboard";
import EmployeeComplianceCenter from "./components/employee/Compliance/EmployeeComplianceCenter";

// Store
import { useAuthStore } from "./store/authStore";

// Database initialization
import { initializeDatabase } from "./lib/db";

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
  const { user, isAuthenticated } = useAuthStore();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Check if non-admin user needs to complete onboarding
  // Admin users don't need to complete onboarding themselves
  if (user.role !== 'admin' && !user.onboardingComplete && !window.location.pathname.includes('/onboarding')) {
    return <Navigate to="/onboarding" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    const redirectPath =
      user.role === "admin"
        ? "/admin"
        : user.role === "ceo"
        ? "/ceo"
        : user.role === "supervisor"
        ? "/supervisor"
        : "/employee";
    return <Navigate to={redirectPath} replace />;
  }

  return children;
}

// Home redirect based on role
function HomeRedirect() {
  const { user, isAuthenticated } = useAuthStore();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Check if non-admin user needs to complete onboarding
  if (user.role !== 'admin' && !user.onboardingComplete) {
    return <Navigate to="/onboarding" replace />;
  }

  const redirectPath =
    user.role === "admin"
      ? "/admin"
      : user.role === "ceo"
      ? "/ceo"
      : user.role === "supervisor"
      ? "/supervisor"
      : "/employee";
  return <Navigate to={redirectPath} replace />;
}

// Onboarding Route - only for authenticated users who haven't completed onboarding
function OnboardingRoute() {
  const { user, isAuthenticated } = useAuthStore();

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // If user has already completed onboarding, redirect to their dashboard
  if (user.onboardingComplete) {
    const redirectPath =
      user.role === "admin"
        ? "/admin"
        : user.role === "ceo"
        ? "/ceo"
        : user.role === "supervisor"
        ? "/supervisor"
        : "/employee";
    return <Navigate to={redirectPath} replace />;
  }

  // Use simplified onboarding that redirects to compliance center
  return <EmployeeOnboardingSimplified />;
}

function App() {
  useEffect(() => {
    initializeDatabase();
  }, []);

  return (
    <div className="App">
      <ConfirmDialogProvider>
        <Toaster position="top-right" richColors duration={2000} />
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

          {/* Onboarding Route for new employees */}
          <Route path="/onboarding" element={<OnboardingRoute />} />
          
          {/* Enhanced Compliance Onboarding (Alternative) */}
          <Route path="/onboarding/compliance" element={
            <ProtectedRoute allowedRoles={["employee", "supervisor", "ceo"]}>
              <ComplianceOnboardingWizard />
            </ProtectedRoute>
          } />

          {/* Home redirect */}
          <Route path="/" element={<HomeRedirect />} />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="employees" element={<EmployeesPage />} />
            <Route path="attendance" element={<AttendanceManager />} />
            <Route path="appraisals" element={<AppraisalsPage />} />
            <Route
              path="appraisals/:id"
              element={<SupervisorAppraisalReview />}
            />
            <Route path="templates" element={<TemplateBuilder />} />
            <Route path="courses" element={<CoursesPage />} />
            <Route path="feedback" element={<AdminFeedbackPage />} />
            <Route path="library" element={<FeedbackLibrary />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="invites" element={<InvitesPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="settings" element={<SettingsPage />} />
            {/* Finance Dashboard */}
            <Route path="finance" element={<FinanceDashboard />} />
            {/* Unified Payroll Routes */}
            <Route path="payroll" element={<UnifiedPayrollDashboard />} />
            <Route path="payroll/run" element={<USPayrollRun />} />
            <Route path="payroll/automation" element={<PayrollAutomation />} />
            <Route path="payroll/salaries" element={<SalaryStructures />} />
            <Route path="payroll/bonuses" element={<BonusManagement />} />
            <Route path="payroll/reimbursements" element={<ReimbursementManagement />} />
            <Route path="payroll/payslips" element={<PayslipViewer />} />
            <Route path="payroll/reports" element={<USPayrollReports />} />
            <Route path="payroll/settings" element={<PayrollSettings />} />
            {/* Payroll Configuration */}
            <Route path="payroll/company-tax" element={<CompanyTaxSettings />} />
            <Route path="payroll/employee-setup" element={<EmployeeCompensationSetup />} />
            <Route path="payroll/pay-groups" element={<PayGroupManagement />} />
            <Route path="payroll/pay-codes" element={<PayCodeManagement />} />
            {/* Tax & Compliance */}
            <Route path="payroll/tax-liabilities" element={<TaxLiabilityManagement />} />
            <Route path="payroll/year-end" element={<YearEndForms />} />
            <Route path="payroll/state-taxes" element={<StateTaxSettings />} />
            <Route path="payroll/attendance" element={<MonthlyAttendanceManagement />} />
            {/* Compliance Routes */}
            <Route path="compliance" element={<ComplianceDashboard />} />
          </Route>

          {/* CEO Routes */}
          <Route
            path="/ceo"
            element={
              <ProtectedRoute allowedRoles={["ceo"]}>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<CEODashboard />} />
            <Route path="appraisals" element={<CEOAppraisalsPage />} />
            <Route path="appraisals/:id" element={<CEOAppraisalReview />} />
            <Route path="assign" element={<CEOAssignAppraisal />} />
            <Route path="templates" element={<TemplateBuilder />} />
            <Route path="team" element={<CEOTeamPage />} />
            <Route path="feedback" element={<CEOFeedbackPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="settings" element={<SettingsPage />} />
            {/* CEO Payroll Routes */}
            <Route path="payroll" element={<CEOPayrollOverview />} />
            <Route path="payroll/reimbursements" element={<CEOReimbursementReview />} />
            <Route path="payroll/leave" element={<CEOLeaveReview />} />
            {/* CEO Personal Payroll Routes */}
            <Route path="my-payslips" element={<MyPayslips />} />
            <Route path="my-salary" element={<MySalaryInfo />} />
            <Route path="my-reimbursements" element={<ReimbursementRequest />} />
            <Route path="my-leave" element={<LeaveBalance />} />
            <Route path="attendance" element={<AttendancePage />} />
          </Route>

          {/* Supervisor Routes */}
          <Route
            path="/supervisor"
            element={
              <ProtectedRoute allowedRoles={["supervisor"]}>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<SupervisorDashboard />} />
            <Route path="team" element={<TeamPage />} />
            <Route path="appraisals" element={<TeamAppraisalsPage />} />
            <Route path="courses" element={<TeamCoursesPage />} />
            <Route
              path="appraisals/:id"
              element={<SupervisorAppraisalReview />}
            />
            <Route path="my-appraisal" element={<MyAppraisalsPage />} />
            <Route path="my-appraisals/:id" element={<AppraisalDetail />} />
            <Route path="feedback" element={<SupervisorFeedbackPage />} />
            <Route path="goals" element={<SupervisorGoalsPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="settings" element={<SettingsPage />} />
            {/* Supervisor Payroll Routes */}
            <Route path="payroll" element={<TeamPayroll />} />
            <Route path="payroll/reimbursements" element={<ApproveReimbursements />} />
            <Route path="payroll/leave" element={<LeaveManagement />} />
            {/* Supervisor Personal Payroll Routes (as an employee) */}
            <Route path="my-payslips" element={<MyPayslips />} />
            <Route path="my-salary" element={<MySalaryInfo />} />
            <Route path="my-reimbursements" element={<ReimbursementRequest />} />
            <Route path="my-leave" element={<LeaveBalance />} />
            <Route path="attendance" element={<AttendancePage />} />
          </Route>

          {/* Employee Routes */}
          <Route
            path="/employee"
            element={
              <ProtectedRoute allowedRoles={["employee"]}>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<EmployeeDashboard />} />
            <Route path="appraisals" element={<MyAppraisalsPage />} />
            <Route path="courses" element={<MyCoursesPage />} />
            <Route path="appraisals/:id" element={<AppraisalDetail />} />
            <Route path="goals" element={<GoalsPage />} />
            <Route path="feedback" element={<FeedbackPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="settings" element={<SettingsPage />} />
            {/* Employee Payroll Routes */}
            <Route path="payroll" element={<MyPayslips />} />
            <Route path="payroll/salary" element={<MySalaryInfo />} />
            <Route path="payroll/reimbursements" element={<ReimbursementRequest />} />
            <Route path="payroll/leave" element={<LeaveBalance />} />
            <Route path="attendance" element={<AttendancePage />} />
            {/* Employee Compliance */}
            <Route path="compliance" element={<EmployeeComplianceCenter />} />
          </Route>

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </ConfirmDialogProvider>
    </div>
  );
}

export default App;
